import { ReactNode } from 'react';
import {
  DevHubAlert,
  DevHubBox,
  DevHubButton,
  DevHubCircularProgress,
  DevHubColorProps,
  DevHubDynamicIcon,
  DevHubModal,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { IconChoices } from '@devxdiscover/devhub-ui/dist/components/icons/IconProps';

interface ConfirmModalProps {
  open: boolean;
  /** Optional heading rendered above the message */
  title?: string;
  /** Main body content — plain text or JSX */
  message: ReactNode;
  /** If provided, rendered as a red error alert below the message (irreversibility warning) */
  warningMessage?: string;
  /** If provided, rendered as a red error alert (operation/mutation error) */
  error?: string;
  /** Label for the confirm button. Defaults to "I Understand & Agree" */
  confirmLabel?: string;
  /** Label for the cancel button. Defaults to "Cancel" */
  cancelLabel?: string;
  /** DevHubButton buttonColor for the confirm button. Defaults to "blueGreen" */
  confirmButtonColor?: DevHubColorProps;
  /** DevHubButton buttonColor for the cancel button. Defaults to "orangeRed" */
  cancelButtonColor?: DevHubColorProps;
  /**
   * Text color applied to the cancel button.
   * Note: gradient colors (e.g. "orangeRed", "blueGreen") are not valid CSS color values
   * and cannot be used here — use solid colors like "red", "orange", "blue" instead.
   */
  cancelTextColor?: DevHubColorProps;
  /**
   * Text color applied to the confirm button.
   * Note: gradient colors (e.g. "orangeRed", "blueGreen") are not valid CSS color values
   * and cannot be used here — use solid colors like "red", "orange", "blue" instead.
   */
  confirmTextColor?: DevHubColorProps;
  /** Icon name to show on the confirm button (from DevHubDynamicIcon) */
  confirmIcon?: IconChoices;
  /** Button variant applied to both Cancel and Confirm buttons. Defaults to "contained" */
  variant?: 'contained' | 'outlined' | 'text';
  /** When true, shows a spinner and disables both buttons */
  isLoading?: boolean;
  /** When true, disables only the confirm button (cancel remains active) */
  confirmDisabled?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void | Promise<void>;
}

/**
 * Generic confirmation modal used across the application for destructive or irreversible actions.
 * Renders a DevHubModal with a message, optional warning/error banners, and Cancel/Confirm buttons.
 * @param props - Component props
 * @param props.open - Whether the modal is visible
 * @param props.title - Optional heading rendered above the message
 * @param props.message - Main body content
 * @param props.warningMessage - Red alert shown below the message for irreversibility warnings
 * @param props.error - Red alert shown below warningMessage for operation errors
 * @param props.confirmLabel - Confirm button label (default: "I Understand & Agree")
 * @param props.cancelLabel - Cancel button label (default: "Cancel")
 * @param props.confirmButtonColor - DevHubButton buttonColor for confirm (default: "blueGreen")
 * @param props.cancelButtonColor - DevHubButton buttonColor for cancel (default: "orangeRed")
 * @param props.cancelTextColor - Explicit text color for the cancel button;
 * @param props.confirmTextColor - Explicit text color for the confirm button;
 * @param props.confirmIcon - Icon name for the confirm button start icon
 * @param props.variant - Button variant for both buttons (default: "contained")
 * @param props.isLoading - Shows spinner and disables buttons while true
 * @param props.confirmDisabled - Disables only the confirm button, keeping cancel active
 * @param props.onConfirm - Called when the user clicks the confirm button; may be async
 * @param props.onCancel - Called when the user clicks Cancel or closes the modal; may be async
 */
export const ConfirmModal = ({
  open,
  title,
  message,
  warningMessage,
  error,
  confirmLabel = 'I Understand & Agree',
  cancelLabel = 'Cancel',
  confirmButtonColor = 'blueGreen',
  cancelButtonColor = 'orangeRed',
  cancelTextColor,
  confirmTextColor,
  confirmIcon,
  variant = 'contained',
  isLoading = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: ConfirmModalProps) => {
  const handleClose = () => {
    if (isLoading) {
      return;
    }

    onCancel();
  };

  const renderMessage = () => {
    if (typeof message === 'string' || typeof message === 'number') {
      return <DevHubTypography variant="body1">{message}</DevHubTypography>;
    }

    return message;
  };

  const resolveConfirmIcon = () => {
    if (isLoading) return <DevHubCircularProgress size={16} />;
    if (confirmIcon) return <DevHubDynamicIcon iconName={confirmIcon} />;
    return undefined;
  };

  return (
    <DevHubModal open={open} onClose={handleClose}>
      <DevHubBox p={3}>
        {title && (
          <DevHubBox mb={2}>
            <DevHubTypography variant="h6">{title}</DevHubTypography>
          </DevHubBox>
        )}
        {renderMessage()}
        {warningMessage && (
          <DevHubBox mt={1}>
            <DevHubAlert severity="error">{warningMessage}</DevHubAlert>
          </DevHubBox>
        )}
        {error && (
          <DevHubBox mt={1}>
            <DevHubAlert severity="error">{error}</DevHubAlert>
          </DevHubBox>
        )}
        <DevHubBox mt={3} display="flex" justifyContent="space-between">
          <DevHubButton
            variant={variant}
            buttonColor={variant !== 'text' ? cancelButtonColor : undefined}
            textColor={cancelTextColor}
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancelLabel}
          </DevHubButton>
          <DevHubButton
            variant={variant}
            buttonColor={variant !== 'text' ? confirmButtonColor : undefined}
            textColor={confirmTextColor}
            onClick={onConfirm}
            disabled={isLoading || confirmDisabled}
            startIcon={resolveConfirmIcon()}
          >
            {isLoading ? 'Please wait...' : confirmLabel}
          </DevHubButton>
        </DevHubBox>
      </DevHubBox>
    </DevHubModal>
  );
};
